import React, { useEffect, useRef, useState } from "react";
import {
  preUploadMemberFile,
  sampleUploadMemberFile,
  memberUploadStatus,
} from "../../../services/met/Service";

const POLL_INTERVAL_MS = 2000;
const MAX_FILE_SIZE = 10 * 1024 * 1024;

const MemberBulkUpload = () => {
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [job, setJob] = useState(null);
  const [error, setError] = useState("");
  const pollRef = useRef(null);
  const fileInputRef = useRef(null);

  const processing =
    uploading || (job && ["queued", "processing"].includes(job.status));

  const stopPolling = () => {
    if (pollRef.current) {
      clearTimeout(pollRef.current);
      pollRef.current = null;
    }
  };

  useEffect(() => stopPolling, []);

  const handleFileChange = (e) => {
    const selected = e.target.files[0] || null;
    setJob(null);
    setError("");
    if (!selected) {
      setFile(null);
      return;
    }
    if (!selected.name.toLowerCase().endsWith(".csv")) {
      setError("Only CSV files are supported");
      setFile(null);
      e.target.value = "";
      return;
    }
    if (selected.size > MAX_FILE_SIZE) {
      setError("File is too large (max 10 MB)");
      setFile(null);
      e.target.value = "";
      return;
    }
    setFile(selected);
  };

  const pollStatus = (jobId, failures = 0) => {
    pollRef.current = setTimeout(async () => {
      try {
        const res = await memberUploadStatus(jobId);
        const data = res.data?.data || {};
        setJob(data);
        if (["queued", "processing"].includes(data.status)) {
          pollStatus(jobId);
        } else if (data.status === "completed") {
          setFile(null);
          if (fileInputRef.current) fileInputRef.current.value = "";
        }
      } catch (err) {
        // Transient network/gateway errors must not abort a running job.
        if (failures < 5) {
          pollStatus(jobId, failures + 1);
        } else {
          setError(
            "Lost connection while checking progress. The upload may still be running on the server; reopen this window and check the member list."
          );
        }
      }
    }, POLL_INTERVAL_MS);
  };

  const handleUpload = async () => {
    if (!file) {
      setError("Please select a file");
      return;
    }

    const formData = new FormData();
    formData.append("file", file);

    try {
      setUploading(true);
      setError("");
      setJob(null);
      // timeout: 0 = no client-side timeout for sending the file itself
      const res = await preUploadMemberFile(formData);
      const body = res.data;
      const jobId = body?.data?.jobId;
      if (!jobId) {
        setError(body?.data?.message || "Upload failed. Please try again.");
        return;
      }
      setJob({ status: "queued", processed: 0 });
      pollStatus(jobId);
    } catch (err) {
      console.error(err);
      setError(
        err.response?.data?.data?.message ||
          err.response?.data?.message ||
          "Upload failed. Please try again."
      );
    } finally {
      setUploading(false);
    }
  };

  const downloadSample = async () => {
    try {
      const res = await sampleUploadMemberFile();

      // BOM so Excel reads UTF-8 correctly
      const blob = new Blob(["﻿", res.data], {
        type: "text/csv;charset=utf-8;",
      });

      const url = window.URL.createObjectURL(blob);

      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", "member_sample.csv");

      document.body.appendChild(link);
      link.click();

      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error("Download failed", err);
      setError("Could not download the sample file.");
    }
  };

  const statusLabel = {
    queued: "Queued...",
    processing: "Processing...",
  };

  return (
    <div className="tw-w-full">
      <div className="tw-w-full">
        <h2 className="tw-text-xl tw-font-bold tw-text-gray-800 tw-mb-4">
          Member Bulk Upload
        </h2>

        <button
          type="button"
          onClick={downloadSample}
          className="tw-bg-blue-500 hover:tw-bg-blue-600 tw-text-white tw-px-4 tw-py-2 tw-rounded-lg tw-mb-4"
        >
          Download Sample File
        </button>

        <div className="tw-mb-4">
          <input
            ref={fileInputRef}
            type="file"
            accept=".csv"
            onChange={handleFileChange}
            disabled={processing}
            className="tw-w-full tw-border tw-border-gray-300 tw-rounded-lg tw-p-2"
          />
          <p className="tw-text-xs tw-text-gray-500 tw-mt-1">
            CSV only, up to 10 MB. Large files (5,000+ members) are processed
            in the background.
          </p>
        </div>

        <button
          type="button"
          onClick={handleUpload}
          disabled={processing || !file}
          className={`tw-w-full tw-py-2 tw-rounded-lg tw-text-white ${
            processing || !file
              ? "tw-bg-gray-400 tw-cursor-not-allowed"
              : "tw-bg-green-500 hover:tw-bg-green-600"
          }`}
        >
          {uploading
            ? "Uploading..."
            : processing
            ? "Processing..."
            : "Upload File"}
        </button>

        {error && (
          <div role="alert" className="tw-mt-4 tw-text-red-500 tw-text-sm">
            {error}
          </div>
        )}

        {job && (
          <div
            role="status"
            className="tw-mt-6 tw-bg-gray-50 tw-p-4 tw-rounded-lg tw-border"
          >
            <h3 className="tw-text-lg tw-font-semibold tw-mb-2">
              Upload Result
            </h3>

            {statusLabel[job.status] && (
              <div className="tw-text-sm tw-text-gray-700">
                {statusLabel[job.status]}
                {job.processed ? ` ${job.processed} rows read` : ""}
              </div>
            )}

            {job.status === "failed" && (
              <div className="tw-text-sm tw-text-red-500">
                {job.message || "Upload failed"}
              </div>
            )}

            {(job.status === "completed" ||
              (job.status === "processing" && job.processed > 0)) && (
              <ul className="tw-text-sm tw-text-gray-700 tw-list-disc tw-pl-5">
                <li className="tw-text-green-600">
                  New members added: {job.inserted ?? 0}
                </li>
                <li className="tw-text-green-600">
                  Existing members re-uploaded (treated as new):{" "}
                  {job.reset_existing ?? 0}
                </li>
                <li>
                  Duplicates in file (skipped):{" "}
                  {job.skipped_duplicate_in_file ?? 0}
                </li>
                <li>Invalid rows (skipped): {job.skipped_invalid ?? 0}</li>
              </ul>
            )}

            {job.errors && job.errors.length > 0 && (
              <details className="tw-mt-3 tw-text-xs tw-text-red-600">
                <summary>Show row errors ({job.errors.length})</summary>
                <ul className="tw-list-disc tw-pl-5 tw-max-h-40 tw-overflow-auto">
                  {job.errors.map((msg, i) => (
                    <li key={i}>{msg}</li>
                  ))}
                </ul>
              </details>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default MemberBulkUpload;
